import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import * as s from './GuestCounter.module.css';
import config from '../../../../dashboard_config.json';

const {
  guestCountForAlarm,
  guestCountForAlert,
  guestFreeTrialText,
  guestOrderCanceledText
} = config.dashboard.guests.guestCounter;

export const GuestCounter = ({ guestsTotal, guestsTotalHistory, guestsRemain, guestsFreeTrialRemain, guestsMax }) => {

  const showFreeTrialText = guestsFreeTrialRemain >= 0 && (guestsRemain === 0 && guestsTotal === 0 && guestsTotalHistory === 0);
  const isCanceledOrder = guestsTotalHistory !== 0 && (guestsFreeTrialRemain === 0 && guestsRemain === 0 && guestsTotal === 0);

  const displayGuestsCounter = useMemo(() => {
    if (showFreeTrialText) {
      return `${guestsFreeTrialRemain} ${guestFreeTrialText}`;
    } else if (isCanceledOrder) {
      return `${guestsFreeTrialRemain} ${guestOrderCanceledText}`;
    } else if (guestsRemain >= 0) {
      return `${guestsRemain}/${guestsTotal}`;
    }
    return '';
  }, [showFreeTrialText, isCanceledOrder, guestsFreeTrialRemain, guestsRemain, guestsTotal]);


  /* 
    Redesign Logic:
    - Normal (Online/Green-ish): rgba(40, 167, 69, 0.2) + #28a745
    - Alarm (Broadcasting/Warning/Yellow-ish): rgba(255, 193, 7, 0.2) + #ffc107 (or config yellow)
    - Alert (Offline/Red-ish): rgba(220, 53, 69, 0.2) + #dc3545
    - Default/Neutral: rgba(108, 117, 125, 0.2) + #aeb5bc
  */

  const badgeStyle = useMemo(() => {
    let style = {
      backgroundColor: 'rgba(108, 117, 125, 0.2)', // Default grey
      color: '#aeb5bc',
      border: '1px solid rgba(108, 117, 125, 0.3)'
    };

    if (guestsTotal !== 0) {
      if (guestsRemain <= guestCountForAlert) {
        // Alert - Red
        style = {
          backgroundColor: 'rgba(220, 53, 69, 0.2)',
          color: '#dc3545',
          border: '1px solid rgba(220, 53, 69, 0.3)'
        };
      } else if (guestsRemain <= guestCountForAlarm) {
        // Alarm - Yellow/Warning
        style = {
          backgroundColor: 'rgba(255, 193, 7, 0.15)',
          color: '#ffc107',
          border: '1px solid rgba(255, 193, 7, 0.3)'
        };
      } else {
        // Normal - Green
        style = {
          backgroundColor: 'rgba(40, 167, 69, 0.2)',
          color: '#28a745',
          border: '1px solid rgba(40, 167, 69, 0.3)'
        };
      }
    }
    return style;
  }, [guestsTotal, guestsRemain]);

  const title = useMemo(() => {
    const usedGuestInvitations = guestsTotal - guestsRemain;
    if (showFreeTrialText || usedGuestInvitations === 0) {
      return '';
    }
    return `Used Guest Invitations: ${usedGuestInvitations}`;
  }, [guestsFreeTrialRemain, guestsRemain, showFreeTrialText]);

  return (
    <div className={s.root} style={badgeStyle} title={title}>
      <span>{displayGuestsCounter}</span>
    </div>
  );
};

GuestCounter.propTypes = {
  guestsTotal: PropTypes.number.isRequired,
  guestsTotalHistory: PropTypes.number.isRequired,
  guestsRemain: PropTypes.number.isRequired,
  guestsFreeTrialRemain: PropTypes.number.isRequired,
  guestsMax: PropTypes.number.isRequired,
};
